import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| TYPES ||============================== //

interface SubscriptionData {
  id: string;
  planCode: string;
  planDisplayName: string;
  status: string;
  acuAllocation: number;
  currentBalance: number;
  startDate: string;
  renewalDate: string;
  billingPeriod?: 'monthly' | 'annual';
}

interface PlanData {
  code: string;
  displayName: string;
  monthlyPriceUsd: number;
  annualPriceUsd?: number;
  includedAcu: number;
}

interface UsageData {
  totalDeducted: number;
  totalAdded: number;
  netChange: number;
  transactionCount: number;
}

interface TierUsage {
  isFreePlan: boolean;
  usage: {
    projects: number;
    repositories: number;
    teamMembers: number;
  };
  limits: {
    maxProjects: number | null;
    maxRepositories: number | null;
    maxTeamMembers: number | null;
  };
  limitsReached: string[];
}

interface CurrentSubscription {
  subscription: SubscriptionData;
  plan: PlanData;
  pricePerAcu?: number; // From system_settings, used for ACU calculations
  hasActiveSubscription?: boolean;
  currentBalance?: number; // Can be at root level when no subscription
  tierUsage?: TierUsage; // Tier usage data for free plan limit checking
}

interface CurrentUsage {
  usage: UsageData;
  startDate: string;
  endDate: string;
  currentBalance: number;
  acuAllocation: number;
}

interface BillingHistoryItem {
  id: string;
  type: 'invoice' | 'top-up';
  date: string;
  description: string;
  amount: number;
  currency: 'usd' | 'acu';
  status: 'paid' | 'pending' | 'failed' | 'completed';
  invoiceUrl?: string;
  invoicePdfUrl?: string;
  metadata?: Record<string, any>;
}

interface BillingHistory {
  items: BillingHistoryItem[];
  total: number;
}

interface RunningAgent {
  id: string;
  type: 'jira' | 'asana' | 'pr_review' | 'insights' | 'ticket_chat';
  taskName: string;
  projectName: string;
  repoName?: string;
  externalId?: string;
  startedAt: string;
  acuUsed: number;
  cancelType: 'task' | 'insight' | 'pr_review' | 'chat_session';
  cancelId: string;
  projectId?: string;
  status?: 'active' | 'queued';
}

interface SubscriptionsState {
  current: CurrentSubscription | null;
  usage: CurrentUsage | null;
  billingHistory: BillingHistory | null;
  runningAgents: RunningAgent[];
  runningAgentsStatus: 'idle' | 'loading' | 'success' | 'failed';
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
  // Per-thunk in-flight flags for deduplication
  subscriptionFetching: boolean;
  usageFetching: boolean;
}

// ==============================|| INITIAL STATE ||============================== //

const initialState: SubscriptionsState = {
  current: null,
  usage: null,
  billingHistory: null,
  runningAgents: [],
  runningAgentsStatus: 'idle',
  status: 'idle',
  error: '',
  subscriptionFetching: false,
  usageFetching: false,
};

// ==============================|| ASYNC THUNKS ||============================== //

export const fetchCurrentSubscription = createAsyncThunk(
  'subscriptions/fetchCurrent',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/subscriptions/me');
      // Unwrap the response object (API returns { response: { subscription, plan } })
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
  {
    // Skip if a fetch is already in-flight (prevents duplicate calls from dashboard + sidebar)
    condition: (_, { getState }) => {
      const { subscriptions } = getState() as { subscriptions: SubscriptionsState };
      return !subscriptions.subscriptionFetching;
    },
  }
);

export const fetchCurrentUsage = createAsyncThunk(
  'subscriptions/fetchUsage',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/subscriptions/me/usage');
      // Unwrap the response object (API returns { response: { usage, ... } })
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
  {
    // Skip if a fetch is already in-flight (prevents duplicate calls from dashboard + sidebar)
    condition: (_, { getState }) => {
      const { subscriptions } = getState() as { subscriptions: SubscriptionsState };
      return !subscriptions.usageFetching;
    },
  }
);

export const topUpCredits = createAsyncThunk(
  'subscriptions/topUp',
  async (payload: { amountUsd: number; description?: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/subscriptions/me/credits/top-up', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to purchase credits');
    }
  }
);

// Stripe: Create PaymentIntent for ACU top-up
// SECURITY: organizationId now extracted from user context on backend
export const createPaymentIntent = createAsyncThunk(
  'subscriptions/createPaymentIntent',
  async (payload: { amountUsd: number }, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/billing/top-up/payment-intent', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create payment intent');
    }
  }
);

// Stripe: Create Checkout Session for ACU top-up (redirects to Stripe hosted page)
// SECURITY: organizationId now extracted from user context on backend
export const createCheckoutSession = createAsyncThunk(
  'subscriptions/createCheckoutSession',
  async (payload: { amountUsd: number; successUrl: string; cancelUrl: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/billing/top-up/checkout-session', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create checkout session');
    }
  }
);

// Stripe: Create Customer Portal session
// SECURITY: organizationId now extracted from user context on backend
export const createCustomerPortalSession = createAsyncThunk(
  'subscriptions/createCustomerPortalSession',
  async (payload: { returnUrl: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/billing/customer-portal/session', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create customer portal session');
    }
  }
);

// Stripe: Preview plan change
// SECURITY: organizationId now extracted from user context on backend
export const previewPlanChange = createAsyncThunk(
  'subscriptions/previewPlanChange',
  async (payload: { newPlanId: string; billingPeriod?: 'monthly' | 'annual' }, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/billing/preview-change', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      // Unwrap the response object (API returns { response: { ... }, metadata: { ... } })
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to preview plan change');
    }
  }
);

// Stripe: Change subscription plan
// SECURITY: organizationId now extracted from user context on backend
export const changePlan = createAsyncThunk(
  'subscriptions/changePlan',
  async (payload: { newPlanId: string; billingPeriod?: 'monthly' | 'annual' }, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/billing/change-plan', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to change plan');
    }
  }
);

// Stripe: Subscribe to a plan (initial subscription)
// SECURITY: organizationId now extracted from user context on backend
export const subscribeToPlan = createAsyncThunk(
  'subscriptions/subscribe',
  async (payload: { planId: string; successUrl: string; cancelUrl: string; billingPeriod?: 'monthly' | 'annual' }, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/billing/subscribe', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to subscribe');
    }
  }
);

// Stripe: Cancel subscription
// SECURITY: organizationId now extracted from user context on backend
export const cancelSubscription = createAsyncThunk(
  'subscriptions/cancel',
  async (payload: { immediate?: boolean }, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/billing/cancel', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to cancel subscription');
    }
  }
);

// Fetch running agents
export const fetchRunningAgents = createAsyncThunk(
  'subscriptions/fetchRunningAgents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest('/subscriptions/me/running-agents');
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch billing history (invoices + top-ups)
export const fetchBillingHistory = createAsyncThunk(
  'subscriptions/fetchBillingHistory',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/billing/history/${organizationId}`);
      // Unwrap the response object (API returns { response: { items, total } })
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch billing history');
    }
  }
);

// ==============================|| SLICE ||============================== //

const subscriptionsSlice = createSlice({
  name: 'subscriptions',
  initialState,
  reducers: {
    resetSubscriptionsState: (state) => {
      state.current = null;
      state.usage = null;
      state.status = 'idle';
      state.error = '';
    },
    // Optimistically update subscription plan after successful change
    updateSubscriptionPlan: (state, action) => {
      if (state.current?.subscription) {
        const { planCode, planDisplayName, monthlyPriceUsd, includedAcu, billingPeriod } = action.payload;
        state.current.subscription.planCode = planCode;
        state.current.subscription.planDisplayName = planDisplayName;
        state.current.subscription.acuAllocation = includedAcu;
        if (billingPeriod) {
          state.current.subscription.billingPeriod = billingPeriod;
        }
        state.current.plan = {
          code: planCode,
          displayName: planDisplayName,
          monthlyPriceUsd,
          includedAcu
        };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch current subscription
    builder
      .addCase(fetchCurrentSubscription.pending, (state) => {
        state.subscriptionFetching = true;
        state.status = 'loading';
        state.error = '';
      })
      .addCase(fetchCurrentSubscription.fulfilled, (state, action) => {
        state.subscriptionFetching = false;
        state.status = 'success';
        state.current = action.payload;
        state.error = '';
      })
      .addCase(fetchCurrentSubscription.rejected, (state, action) => {
        state.subscriptionFetching = false;
        state.status = 'failed';
        state.error = action.payload as string;
      });

    // Fetch current usage
    builder
      .addCase(fetchCurrentUsage.pending, (state) => {
        state.usageFetching = true;
        state.status = 'loading';
        state.error = '';
      })
      .addCase(fetchCurrentUsage.fulfilled, (state, action) => {
        state.usageFetching = false;
        state.status = 'success';
        state.usage = action.payload;
        state.error = '';
      })
      .addCase(fetchCurrentUsage.rejected, (state, action) => {
        state.usageFetching = false;
        state.status = 'failed';
        state.error = action.payload as string;
      });

    // Top-up credits
    builder
      .addCase(topUpCredits.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(topUpCredits.fulfilled, (state, action) => {
        state.status = 'success';
        // Update current balance if we have subscription data
        if (state.current?.subscription) {
          state.current.subscription.currentBalance = action.payload.newBalance;
        }
        state.error = '';
      })
      .addCase(topUpCredits.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });

    // Create payment intent (Stripe)
    builder
      .addCase(createPaymentIntent.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(createPaymentIntent.fulfilled, (state) => {
        state.status = 'success';
        state.error = '';
      })
      .addCase(createPaymentIntent.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });

    // Create checkout session (Stripe)
    builder
      .addCase(createCheckoutSession.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(createCheckoutSession.fulfilled, (state) => {
        state.status = 'success';
        state.error = '';
      })
      .addCase(createCheckoutSession.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });

    // Create customer portal session (Stripe)
    builder
      .addCase(createCustomerPortalSession.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(createCustomerPortalSession.fulfilled, (state) => {
        state.status = 'success';
        state.error = '';
      })
      .addCase(createCustomerPortalSession.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });

    // Change plan (Stripe)
    builder
      .addCase(changePlan.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(changePlan.fulfilled, (state) => {
        state.status = 'success';
        state.error = '';
        // Subscription will be refetched to show new plan
      })
      .addCase(changePlan.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });

    // Subscribe to plan (Stripe)
    builder
      .addCase(subscribeToPlan.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(subscribeToPlan.fulfilled, (state) => {
        state.status = 'success';
        state.error = '';
        // Subscription will be refetched after successful subscribe
      })
      .addCase(subscribeToPlan.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });

    // Cancel subscription (Stripe)
    builder
      .addCase(cancelSubscription.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(cancelSubscription.fulfilled, (state) => {
        state.status = 'success';
        state.error = '';
        // Subscription will be refetched to show cancelled status
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });

    // Fetch running agents
    builder
      .addCase(fetchRunningAgents.pending, (state) => {
        state.runningAgentsStatus = 'loading';
      })
      .addCase(fetchRunningAgents.fulfilled, (state, action) => {
        state.runningAgentsStatus = 'success';
        state.runningAgents = action.payload || [];
      })
      .addCase(fetchRunningAgents.rejected, (state) => {
        state.runningAgentsStatus = 'failed';
        state.runningAgents = [];
      });

    // Fetch billing history
    builder
      .addCase(fetchBillingHistory.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(fetchBillingHistory.fulfilled, (state, action) => {
        state.status = 'success';
        state.billingHistory = action.payload;
        state.error = '';
      })
      .addCase(fetchBillingHistory.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });
  },
});

export const { resetSubscriptionsState, updateSubscriptionPlan } = subscriptionsSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectCurrentSubscription = (state: any) => state.subscriptions.current;
export const selectCurrentUsage = (state: any) => state.subscriptions.usage;
export const selectBillingHistory = (state: any) => state.subscriptions.billingHistory;
export const selectSubscriptionsStatus = (state: any) => state.subscriptions.status;
export const selectSubscriptionsError = (state: any) => state.subscriptions.error;
export const selectTierUsage = (state: any) => state.subscriptions.current?.tierUsage;
export const selectRunningAgents = (state: any) => state.subscriptions.runningAgents;
export const selectRunningAgentsStatus = (state: any) => state.subscriptions.runningAgentsStatus;

export default subscriptionsSlice.reducer;
